import React, { useCallback, useMemo, useState } from 'react';
import { Tooltip, Text, Badge } from '@wix/wix-base-ui';
import * as util from '#packages/util';
import { translate } from '#packages/i18n';
import type { LanguageDefinition } from '@wix/document-services-types';
import * as stateManagement from '#packages/stateManagement';
import constants from '#packages/constants';
import { Visible } from '@wix/wix-ui-icons-common';
import { Box, Tooltip as WDSTooltip, TextButton } from '@wix/design-system';
import { Trans } from 'react-i18next';
import * as coreBi from '#packages/coreBi';
import experiment from 'experiment';
import type { SendBiFunction } from './languagePanel';

interface LocaleRowProps {
  locale: LanguageDefinition;
  currentLocaleId: string;
  primaryLocaleId: string;
  onSelect(): void;
  index: number;
  flagUrl: string;
  openSettingsPanel: () => void;
  sendBI: SendBiFunction;
  siteId: string;
}

export const LocaleRow: React.FC<LocaleRowProps> = ({
  locale,
  currentLocaleId,
  primaryLocaleId,
  onSelect,
  index,
  flagUrl,
  openSettingsPanel,
  sendBI,
  siteId,
}) => {
  const { LANGUAGE_STATUSES } = constants.MULTILINGUAL;
  const [isRowHovered, setIsRowHovered] = useState(false);

  const handleMouseEnter = useCallback(() => setIsRowHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsRowHovered(false), []);

  const isDefaultViewedEnabled = experiment.isOpen(
    'specs.ml.useDefaultViewedLanguage',
  );

  const localeName = useMemo(
    () => util.multilingual.languages.getLocaleFullName(locale),
    [locale],
  );

  const regionName = useMemo(() => {
    if (!locale.regionCode) {
      return null;
    }

    return stateManagement.multilingual.services.utils.getRegionLocalizedNameByRegionCode(
      locale.regionCode,
    );
  }, [locale.regionCode]);

  const isVisible = useMemo(
    () => locale.status === LANGUAGE_STATUSES.ACTIVE,
    [LANGUAGE_STATUSES.ACTIVE, locale.status],
  );

  const isSelected = useMemo(
    () => locale.id === currentLocaleId,
    [currentLocaleId, locale.id],
  );

  const isPrimaryLocale = useMemo(
    () => locale.id === primaryLocaleId,
    [locale.id, primaryLocaleId],
  );

  const isDefaultViewedLocale = useMemo(
    () => locale.isDefaultViewed,
    [locale.isDefaultViewed],
  );

  const handleSettingsClick = () => {
    if (sendBI && siteId) {
      sendBI(
        coreBi.events.multilingual.multilingualDashboardSettingsClicked,
        {
          primary_lang: primaryLocaleId,
          origin: 'languages panel tooltip',
          main_region_code: locale.regionCode,
          default_viewed_language: locale.languageCode,
          default_viewed_region: locale.regionCode,
        },
        siteId,
      );
    }

    openSettingsPanel();
  };

  const primaryLocaleBadge = (
    <Badge
      skin="standard"
      value={translate('SiteLanguages_Panel_Label_MainLanguage')}
      shouldTranslate={false}
    />
  );

  const defaultViewedLocaleIcon = (
    <WDSTooltip
      content={
        <Trans
          i18nKey="SiteLanguages_Panel_Default_Visitor_Language_Tooltip"
          components={{
            1: (
              <TextButton
                size="small"
                skin="light"
                underline="always"
                onClick={handleSettingsClick}
              />
            ),
          }}
        />
      }
    >
      <Visible alignmentBaseline="middle" />
    </WDSTooltip>
  );

  const visibilityText = (
    <Text
      size={'tiny'}
      light={isSelected && !isRowHovered}
      shouldTranslate={false}
    >
      {translate(
        isVisible
          ? 'SiteLanguages_Panel_Label_Visible'
          : 'SiteLanguages_Panel_Label_Hidden',
      )}
    </Text>
  );

  return (
    <div
      onClick={onSelect}
      className={util.cx('localeRowRoot', {
        localeRowSelected: isSelected,
      })}
      data-aid={`my-site-languages-panel-option-${index}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Tooltip
        content={translate('Multilingual_Mode_LangMenu_Activate_Tooltip', {
          Lang_Name: localeName,
        })}
        shouldTranslate={false}
        disabled={isVisible || isPrimaryLocale}
      >
        <div className="leftSideContainer">
          <div className="flagContainer">
            <img src={flagUrl} alt={locale.languageCode} />
          </div>
          <Box direction="horizontal" verticalAlign="middle" gap="SP1">
            <Box direction="vertical">
              <Text
                size="small"
                weight="thin"
                dataHook={`my-site-languages-panel-option-language-${index}`}
                light={isSelected && !isRowHovered}
                shouldTranslate={false}
              >
                {localeName}
              </Text>
              <Text
                size="tiny"
                weight="thin"
                skin="secondary"
                light={isSelected && !isRowHovered}
                shouldTranslate={false}
              >
                {regionName}
              </Text>
            </Box>
            {isDefaultViewedLocale && isDefaultViewedEnabled
              ? defaultViewedLocaleIcon
              : null}
          </Box>
        </div>
      </Tooltip>

      <div className="rightSideContainer">
        {isPrimaryLocale ? primaryLocaleBadge : visibilityText}
      </div>
    </div>
  );
};
